import { useCallback, useEffect, useRef, useState } from "react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { colors } from "@nextui-org/theme";
import { candidateMatchedJobs, jobSeen } from "@endpoints/job.service";
import { useSnackbar } from "notistack";
import JobCard from "./components/JobCard";
import { SearchIcon } from "lucide-react";
import FilterSection, { jobMatchesFilters, jobMatchesSortingFilters } from "./components/FilterSection";
import JobCard1 from "./components/JobCard1";

const LIMIT = 25

const JobMatchesPage1 = () => {
  const { enqueueSnackbar } = useSnackbar()
  const sentinelRef = useRef(null);
  const scrollListRef = useRef(null);

  const [activeFilter, setActiveFilter] = useState(jobMatchesFilters[0])
  const [activeSortingFilter, setActiveSortingFilter] = useState(jobMatchesSortingFilters[0])
  const [favoriteFilter, setFavoriteFilter] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMoreData, setHasMoreData] = useState(false)

  const [jobList, setJobList] = useState([]);

  const fetchMatchedJobs = useCallback(
    async () => {
      try {
        if (page > 1) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true)
        }
        const { data, success, pagination } = await candidateMatchedJobs({
          page,
          limit: LIMIT,
          type: activeFilter?.value,
          sortBy: activeSortingFilter?.value,
          searchText
        });
        if (success) {
          setHasMoreData(pagination?.hasMore)
          setJobList((prev) => (page === 1 ? data : [...prev, ...data]));
        }
      } catch (error) {
        enqueueSnackbar(error, { variant: 'error' })
        console.error(error);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false)
      }
    },
    [page, activeFilter, activeSortingFilter, searchText]
  );

  const handleJobSeen = async (jobId) => {
    try {
      // setIsLoading(true);
      await jobSeen({ jobId });
    } catch (error) {
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      // setIsLoading(false);
    }
  };

  // Load more for pagination
  const handleLoadMore = useCallback(
    () => {
      if (!hasMoreData || isLoadingMore || isLoading) return
      setPage((prev) => prev + 1)
    },
    [hasMoreData, page]
  )

  // Init fetch
  useEffect(() => {
    fetchMatchedJobs();
  }, [fetchMatchedJobs]);


  // Observer for infinite scrolling
  useEffect(() => {
    if (!sentinelRef?.current || !scrollListRef?.current) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: scrollListRef?.current,
        rootMargin: '0px',
        threshold: 1
      }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [isLoading, isLoadingMore]);

  return (
    <div className="flex flex-col flex-1 rounded-lg bg-white w-full overflow-hidden min-h-0">
      {/* Top container */}
      <div className="flex items-center justify-between border-b py-3 pl-2">
        {/* Search container */}
        <div className="flex items-center border rounded-md text-xs font-medium h-9 px-3.5 bg-gray-50">
          <input
            className="h-full flex-1 bg-transparent w-56 outline-none"
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e?.target?.value)}
          />
          <SearchIcon color="gray" size={16} />
        </div>
        {/* Filter dropdown */}
        <FilterSection
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          activeSortingFilter={activeSortingFilter}
          setActiveSortingFilter={setActiveSortingFilter}
          favoriteFilter={favoriteFilter}
          setFavoriteFilter={setFavoriteFilter}
        />

      </div>
      {/* List section */}
      <div
        className="overflow-y-scroll no-scrollbar flex-1 flex flex-col min-h-0"
        ref={scrollListRef}
      >
        {
          isLoading ?
            <div className="m-auto py-4">
              <LoadingAnimation color={colors.black} className="w-5 h-5" />
            </div>
            :
            !jobList?.length ?
              <div className="flex flex-1 justify-center items-center">
                <p className="font-medium text-sm text-center text-gray-500">
                  No matching jobs found yet.
                </p>
              </div>
              :
              <>
                {
                  jobList.map((data, index) => (
                    <JobCard1 data={data} onClick={() => handleJobSeen(data?.jobId)} />
                  ))
                }
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-4 gap-2">
                    <LoadingAnimation color={colors.black} className="w-5 h-5" />
                    <span className="font-medium text-xs italic text-light-black">Loading more data</span>
                  </div>
                )}
                {/* Sentinel Observer */}
                <div ref={sentinelRef} className="h-px" />
              </>
        }

      </div>
    </div>
  );
};

export default JobMatchesPage1;
